/**
 * OvergrownConservatory -- Level 2.
 *
 * An underground warren doubling as a frantic steampunk kitchen.
 * Theme: Sequencing and Regulating.
 *
 * Per the 2026-04-14 companion book canon rewrite, the level runs on the
 * Incubation/Irrigation cycle rather than a day/night transition. Basil
 * flips the room at the Master Mist Regulator; Minna threads the room's
 * two distinct dangers (hunting foxes in Incubation, frozen opossums in
 * Irrigation that only walk when it's cool and misty).
 *
 * Puzzles (in order) -- each is a self-contained `PuzzleModule` under
 * `../levels/puzzles/overgrown-conservatory/`:
 *   1. Master Mist Regulator -- `MasterValvePuzzle`
 *   2. Nectar Escort         -- `NectarEscortPuzzle`
 *   3. Boiler-Bound Sundew   -- `BoilerBoundSundewPuzzle` (apex, 3-phase)
 *
 * Elder Gus encounter follows the Apex puzzle -> unlocks Chronos Bubble.
 *
 * This file is now the scene shell only: boot, camera, lighting,
 * post-processing, ConservatoryCycle system, environment art, ambient
 * particles, and the Elder encounter + level completion. All puzzle
 * geometry, NPCs, and win-condition logic lives in the puzzle modules.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import {
  applyVisualProfile,
  applyPostProcessPreset,
  PostProcessPhaseTween,
  OVERGROWN_CONSERVATORY_PROFILE,
  UNDERGROUND_WARMTH,
  CONSERVATORY_IRRIGATION,
  tagAs,
} from "../levels";
import {
  ParallaxBackground,
  paintGradient,
  paintTreeSilhouette,
  paintHazeBand,
} from "../systems/ParallaxBackground";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import "@babylonjs/loaders/glTF";

import { getAssetManager, InstantiatedModel } from "../systems/AssetManager";
import {
  freezeStaticMeshes,
  DEFAULT_STATIC_EXCLUDES,
  scatterInstances,
  type ScatterPlacement,
} from "../levels/staticOptimization";

import { PhysicsSystem } from "../systems/PhysicsSystem";
import { CameraSystem } from "../systems/CameraSystem";
import { InputManager } from "../systems/InputManager";
import { AbilityHolder } from "../systems/AbilitySystem";
import { AudioManager } from "../systems/AudioManager";
import { ConservatoryCycle } from "../systems/ConservatoryCycle";
import { CutsceneSystem, CutsceneStep } from "../systems/CutsceneSystem";
import { SaveSystem } from "../systems/SaveSystem";

import { InteractiveInfo } from "../abilities/minna/AuraSight";
import { ShadowZoneInfo } from "../abilities/minna/ShadowSlip";
import { ResonantDrop } from "../abilities/coop/ResonantDrop";
import { getOrMakeNoiseBump } from "../systems/ProceduralNoise";
import { Portal } from "../entities/interactives/Portal";
import { GreaseCache } from "../entities/interactives/GreaseCache";
import { ChronosBubble } from "../abilities/coop/ChronosBubble";

import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { OVERGROWN_CONSERVATORY, COLORS, WORLD_DEBUG } from "../config/GameConfig";
import { ELDER_GUS_ENCOUNTER, LEVEL_INTRO, controlHintForMode } from "../config/dialogue";

import { setupBaseLevel } from "./BaseLevel";
import { tryCoopAbility } from "./coopAbility";
import { setupElderBiscuitAnchor } from "./setupElderBiscuitAnchor";

import type { PuzzleContext, PuzzleModule, PuzzleState } from "../levels/PuzzleModule";
import { MasterValvePuzzle } from "../levels/puzzles/overgrown-conservatory/MasterValve";
import { NectarEscortPuzzle } from "../levels/puzzles/overgrown-conservatory/NectarEscort";
import { BoilerBoundSundewPuzzle } from "../levels/puzzles/overgrown-conservatory/BoilerBoundSundew";

import { ElderGus } from "../entities/npcs/ElderGus";
import { LevelCueDriver } from "../levels/LevelCueDriver";

export interface OvergrownConservatoryContext {
  physicsSystem: PhysicsSystem;
  cameraSystem: CameraSystem;
  inputManager: InputManager;
  minna: import("../entities/players/Minna").Minna;
  basil: import("../entities/players/Basil").Basil;
  minnaAbilities: AbilityHolder;
  basilAbilities: AbilityHolder;
  audioManager: AudioManager;
  conservatoryCycle: ConservatoryCycle;
  cutsceneSystem: CutsceneSystem;
}

interface ConservatoryShellHandle {
  root: TransformNode;
  applyPhase: (phase: "dawn" | "day" | "dusk" | "midnight") => void;
  dispose: () => void;
}

// ─── Main Setup ─────────────────────────────────────────────────────

/**
 * Set up the Midnight Burrow scene.
 * Call after the engine and scene are created.
 */
export async function setupOvergrownConservatory(scene: Scene): Promise<OvergrownConservatoryContext> {
  // --- Base level setup ---
  const base = await setupBaseLevel(scene, {
    levelKey: "OvergrownConservatory",
    clearColor: Color4.FromHexString(OVERGROWN_CONSERVATORY.COLORS.BURROW_DEEP + "ff"),
    // Match OVERGROWN_CONSERVATORY_PROFILE.fog (profiles.ts) — the visual
    // profile applied just below is authoritative and overrides these a
    // few frames later, but the old BURROW_EARTH brown + 0.025 flashed a
    // muddy first frame before applyVisualProfile ran (Q-039).
    fogColor: new Color3(0.16, 0.33, 0.38),
    fogDensity: 0.006,
    minnaSpawn: new Vector3(5, 3, 0),
    basilSpawn: new Vector3(10, 3, 0),
  });

  const {
    physics: physicsSystem, minna, basil, minnaAbilities, basilAbilities,
    auraSight, shadowSlip, calmingResonance, turnTableTouch, pneumaticAnchor,
    camera: cameraSystem, audioManager, cutsceneSystem, inputManager,
  } = base;

  // --- Visual profile: lights, shadows, post-process, glow, fog, clearColor ---
  const visualHandles = applyVisualProfile(scene, OVERGROWN_CONSERVATORY_PROFILE, {
    glowLayer: base.getGlowLayer(),
  });
  base.addDisposeCallback(() => visualHandles.dispose());

  const glowLayer = visualHandles.glowLayer;
  const shadowGen = visualHandles.shadowGenerator!;
  base.shadowGenerator = shadowGen;

  // --- Conservatory phase cycle ---
  // The visual profile captures the DAY-phase baseline light values;
  // ConservatoryCycle owns the hemispheric + directional lights and
  // lerps them through the dawn/day/dusk/midnight phases at runtime.
  // Canon PR 2 will swap the phase names for incubation/irrigation, but
  // the system still owns the lighting transitions either way.
  const conservatoryCycle = new ConservatoryCycle(
    scene,
    visualHandles.hemiLight,
    visualHandles.keyLight,
  );

  // --- Dual-state post-process (Phase C1) ---
  // Canon L2 is a two-state room: Incubation (blown-out heat) and
  // Irrigation (cool mist). The visual profile seeds UNDERGROUND_WARMTH,
  // but ConservatoryCycle actually boots in `day` (= irrigation) — so
  // the starting post-process and the starting lighting were out of
  // phase, which Gemini 3.1-pro fluidity review caught as "teal lights
  // with warm grade." Sync the initial preset to the cycle's phase so
  // first-frame reads consistently, then keep the swap driven by
  // phase-changed events.
  const initialPostPreset = conservatoryCycle.isIrrigation
    ? CONSERVATORY_IRRIGATION
    : UNDERGROUND_WARMTH;
  applyPostProcessPreset(visualHandles.pipeline, initialPostPreset);
  const postTween = new PostProcessPhaseTween(visualHandles.pipeline, initialPostPreset);
  // Q-034: tween the grade in lockstep with the 1800ms light lerp instead
  // of snapping at lerp end. durationMs rides the event payload so the
  // grade always matches the cycle's actual lerp length.
  base.addUnsub(
    gameEvents.on(GameEvents.CONSERVATORY_CYCLE_TRANSITION, ({ to, durationMs }) => {
      postTween.start(
        to === "irrigation" ? CONSERVATORY_IRRIGATION : UNDERGROUND_WARMTH,
        durationMs,
      );
    }),
  );
  // Settle authority: setPhaseImmediate (force-solve / checkpoint seed)
  // emits only the settled pair — no transition event — so this snap is
  // load-bearing there; after a normal lerp it just pins exact finals.
  base.addUnsub(
    gameEvents.on(GameEvents.CONSERVATORY_PHASE_CHANGED, ({ phase }) => {
      postTween.settle(
        phase === "irrigation" ? CONSERVATORY_IRRIGATION : UNDERGROUND_WARMTH,
      );
    }),
  );

  // --- Parallax jungle-beyond-the-glass (Phase C3) ---
  // Conservatory is a glass dome: beyond its walls the book describes
  // iron-wood vines, bioluminescent ferns, and heat haze. We build two
  // parallax layers of vine silhouettes plus a cool teal mist band so
  // movement inside the dome reads against motion outside.
  const conservatoryParallax = new ParallaxBackground(scene, cameraSystem.camera, [
    {
      name: "conservatoryDeepSky",
      z: -50,
      scrollFactor: 0.95,
      width: 260,
      height: 70,
      yOffset: 12,
      alpha: 0.85,
      paint: paintGradient("#08201c", "#1e3a2e"),
    },
    {
      name: "conservatoryVines",
      z: -22,
      scrollFactor: 0.55,
      width: 220,
      height: 32,
      yOffset: 5,
      alpha: 0.8,
      paint: paintTreeSilhouette("#04100d", 0.5, 0.98, 110),
    },
    {
      // Mist band pushed from z=-6 to z=-14 and alpha reduced from 0.5
      // to 0.22 — the earlier tuning drew a semi-opaque teal curtain
      // over the whole foreground, which Gemini 3.1-pro reported as a
      // black-void L2. The band still tints the midground but no
      // longer blocks the env GLB behind it.
      name: "conservatoryMistBand",
      z: -14,
      scrollFactor: 0.35,
      width: 160,
      height: 18,
      yOffset: 2.2,
      alpha: 0.22,
      paint: paintHazeBand("rgba(70,130,120,0.55)", 0.55),
    },
  ]);
  base.addDisposeCallback(
    base.registerSafeBeforeRender("conservatoryParallax", () =>
      conservatoryParallax.update(),
    ),
  );
  base.addDisposeCallback(() => conservatoryParallax.dispose());

  // --- Environment (Blender-authored glTF) ---
  const envInstance = await loadEnvironment(scene, shadowGen);
  const collisionMeshes =
    envInstance?.meshes.filter(
      (mesh): mesh is Mesh => mesh instanceof Mesh && mesh.name.includes("collision_"),
    ) ?? [];

  // The new overgrown_conservatory_env.glb (authored in Blender during
  // this session) doesn't ship with any `collision_*` named meshes —
  // the old midnight_burrow_env.glb did. Without a fallback, the
  // Conservatory has no physics floor and cats fall into the void on
  // spawn, triggering the death-respawn loop Gemini flagged in the
  // fluidity review. Detect the missing collider and synthesize a
  // long invisible floor slab that spans the playable x-range.
  if (collisionMeshes.length === 0) {
    const W = OVERGROWN_CONSERVATORY.WORLD_WIDTH;
    const floor = MeshBuilder.CreateBox(
      "collision_ground_fallback",
      { width: W + 40, height: 0.5, depth: 4 },
      scene,
    );
    floor.position.set(W / 2 - 10, -0.25, 0);
    // Previously invisible — but the env GLB's `conservatory_floor`
    // mesh is also hidden (axis-swap bug from Blender export), which
    // left the level with no visible ground at all. Gemini 3.1-pro
    // flagged this as "black void, only cats/UI/one valve visible."
    // Give the fallback a PBR loam material so the level actually
    // reads as a floor instead of empty space.
    const floorMat = new PBRMaterial("conservatoryFallbackFloorMat", scene);
    floorMat.albedoColor = new Color3(0.18, 0.14, 0.11);   // warm loam
    floorMat.metallic = 0.0;
    floorMat.roughness = 0.9;
    floorMat.emissiveColor = new Color3(0.02, 0.02, 0.01);
    floor.material = floorMat;
    floor.receiveShadows = true;
    shadowGen.addShadowCaster(floor);
    floor.isPickable = false;
    new PhysicsAggregate(
      floor,
      PhysicsShapeType.BOX,
      { mass: 0, friction: 0.8, restitution: 0.1 },
      scene,
    );
    const grateMat = new PBRMaterial("conservatoryFallbackGrateMat", scene);
    grateMat.albedoColor = Color3.FromHexString(COLORS.BRASS).scale(0.65);
    grateMat.metallic = 0.55;
    grateMat.roughness = 0.5;
    grateMat.emissiveColor = new Color3(0.04, 0.025, 0.01);
    const vineRailMat = new StandardMaterial("conservatoryFallbackVineRailMat", scene);
    vineRailMat.diffuseColor = new Color3(0.16, 0.34, 0.2);
    vineRailMat.emissiveColor = new Color3(0.03, 0.09, 0.04);
    vineRailMat.specularColor = Color3.Black();
    for (let x = 6; x < W - 4; x += 12) {
      const grate = MeshBuilder.CreateBox(
        `conservatoryFallbackGrate_${x}`,
        { width: 0.16, height: 0.06, depth: 3.15 },
        scene,
      );
      grate.position.set(x, 0.08, 0.18);
      grate.material = grateMat;
      grate.receiveShadows = true;
      grate.isPickable = false;
      tagAs(grate, "modular-final");

      const vineRail = MeshBuilder.CreateCylinder(
        `conservatoryFallbackVineRail_${x}`,
        { height: 7.5, diameter: 0.08, tessellation: 8 },
        scene,
      );
      vineRail.rotation.z = Math.PI / 2;
      vineRail.position.set(x + 3.2, 0.24, 1.28);
      vineRail.material = vineRailMat;
      vineRail.receiveShadows = true;
      vineRail.isPickable = false;
      tagAs(vineRail, "modular-final");
    }
    collisionMeshes.push(floor);
    console.warn(
      "[OvergrownConservatory] env GLB has no collision_ground; synthesized fallback floor",
    );
  }

  const escortSupportPlatforms = createEscortSupportPlatforms(scene, physicsSystem);
  for (const platform of escortSupportPlatforms) {
    base.addDisposeCallback(() => platform.dispose());
  }
  const sundewMawPlatform = createSundewMawPlatform(scene, physicsSystem);
  base.addDisposeCallback(() => sundewMawPlatform.dispose());
  const environmentShell = createConservatoryShell(
    scene,
    shadowGen,
    collisionMeshes,
  );
  if (envInstance) {
    environmentShell.root.setEnabled(false);
    const fallbackGround = scene.getMeshByName("collision_ground_fallback");
    if (fallbackGround) {
      fallbackGround.isVisible = false;
    }
  }
  environmentShell.applyPhase(conservatoryCycle.phase);
  base.addUnsub(
    gameEvents.on(GameEvents.DAY_NIGHT_CHANGED, (data) => {
      environmentShell.applyPhase(data.phase as "dawn" | "day" | "dusk" | "midnight");
    }),
  );
  base.addDisposeCallback(() => environmentShell.dispose());
  const heroProps = await loadConservatoryHeroProps(scene, shadowGen);
  for (const prop of heroProps) {
    base.addDisposeCallback(() => prop.dispose());
  }

  // --- L2 hero prop scatter ---
  // Four hero props placed around the dome so it reads as a Victorian
  // glass conservatory overrun by feral sweet-producing flora — per
  // the canon companion book pg 17.
  //
  //   * l2_iron_wood_vine       — Meshy (PBR). Twisted iron-bark ivy along
  //                               the back wall.
  //   * l2_bioluminescent_fern  — Meshy (PBR). Layered fronds with emissive
  //                               veins, clustered in dim corners + at the
  //                               Sundew apex (heat-sensitive flora per canon).
  //   * l2_petrified_layer_cake — Stub (copied from the existing
  //                               `petrified_layer_cake.glb`). Meshy
  //                               generation failed mid-queue with
  //                               "Insufficient credits" on 2026-04-17;
  //                               regenerate via
  //                               tools/meshy/generate-l2-conservatory-props.py
  //                               once credits are topped up. Scattered
  //                               near the MasterValve puzzle valves as
  //                               environmental storytelling (once-a-tea-party).
  //   * l2_glass_dome_panel     — Blender procedural stub
  //                               (tools/meshy/stub-glass-dome-panel.py).
  //                               Hex iron frame + tinted glass, tileable.
  //                               Regenerate via Meshy when credits return.
  //
  // `scatterInstances` fails soft: if any GLB is missing on disk it logs
  // a warning and continues. That keeps the level bootable regardless.
  const VINE_PLACEMENTS: ScatterPlacement[] = [
    // Back-wall drape: varied rotation and scale so the coil reads
    // organic, not stamped. X spans the full 0-175u playable range.
    //
    // Y pulled down from 14 → 6.5 so the vines stay inside the camera
    // frustum. With cats at y≈3, the midpoint-follow camera sits at
    // y=4.2 and the half-fov at z=-5.5 covers roughly y∈[-2.8, 11.2].
    // Vine bounds are ~2m tall (scale ≈1), so centering at y=6.5 puts
    // the drape 5.5-7.5u above the floor, clearly visible against the
    // parallax but not clipping the cats' silhouette. The earlier
    // y=14 placement sat fully above the frustum and Gemini's
    // fluidity pass correctly reported "no background vines visible."
    { x: 8,   y: 6.5, z: -5.5, yaw: 0.2,  scale: 1.10 },
    { x: 22,  y: 6.5, z: -5.2, yaw: -0.3, scale: 0.95 },
    { x: 38,  y: 7.0, z: -5.5, yaw: 0.1,  scale: 1.25 },
    { x: 52,  y: 6.5, z: -5.3, yaw: -0.15, scale: 1.00 },
    { x: 68,  y: 7.0, z: -5.5, yaw: 0.25, scale: 1.15 },
    { x: 84,  y: 6.5, z: -5.4, yaw: -0.1, scale: 0.90 },
    { x: 98,  y: 7.0, z: -5.5, yaw: 0.05, scale: 1.20 },
    { x: 140, y: 6.5, z: -5.3, yaw: 0.35, scale: 1.05 },
  ];
  const CAKE_PLACEMENTS: ScatterPlacement[] = [
    // Scattered near the MasterValve puzzle (x≈22) so the cakes read as
    // "once-a-tea-party, now petrified" environmental storytelling.
    { x: 14, y: 0.4, z: -2.2, yaw: 0.4,  scale: 0.9  },
    { x: 30, y: 0.4, z: -2.3, yaw: -0.2, scale: 1.0  },
    { x: 78, y: 0.4, z: -2.5, yaw: 0.8,  scale: 0.85 },
  ];
  const FERN_PLACEMENTS: ScatterPlacement[] = [
    // Dim-corner clusters — low density between puzzles so they read as
    // organic undergrowth rather than a hedge row. Keep them at least
    // 2.8u behind the cats' z=0 play plane: the Meshy fern bounds are
    // broad enough that the earlier z≈-2 placement could visually
    // graft fronds onto Basil's silhouette in the L2 spawn framing.
    { x: 5,   y: 0.3, z: -6.4, scale: 1.2,  yaw: 0.0  },
    { x: 18,  y: 0.3, z: -6.2, yaw: 0.5,  scale: 0.9  },
    { x: 40,  y: 0.3, z: -6.3, yaw: 0.2,  scale: 1.15 },
    { x: 55,  y: 0.3, z: -6.5, yaw: -0.8, scale: 0.95 },
    { x: 72,  y: 0.3, z: -6.2, yaw: 0.6,  scale: 1.1  },
    { x: 88,  y: 0.3, z: -6.4, yaw: -0.1, scale: 1.0  },
    { x: 102, y: 0.3, z: -6.3, yaw: 0.4,  scale: 1.2  },
    // Sundew apex cluster (x≈133) — the heat-sensitive flora Gus warns
    // about in the Phase 3 dialogue. Tighter cluster + slight emissive
    // boost from the PBR texture means the apex reads pre-ignition.
    { x: 129, y: 0.3, z: -3.3, yaw: 0.7,  scale: 1.3  },
    { x: 137, y: 0.3, z: -3.3, yaw: -0.4, scale: 1.25 },
    { x: 133, y: 0.3, z: -3.6, yaw: 0.0,  scale: 1.4  },
  ];
  // Hexagonal glass dome panels — 12 instances tiled along the ceiling
  // in a 2-row hex grid. Row 1 sits slightly lower + offset so the
  // panels interlock visually, matching a real honeycomb dome skin.
  // z pushed behind the play plane (-8 to -9) so the dome reads as the
  // architectural backdrop, not foreground clutter.
  //
  // Y pulled down from 24/27.4 → 9/11 so the ceiling hex grid reads
  // inside the camera frustum. Even at scale 4.2 (panel edge ~8m at
  // the bounds extent of ±1.98m) the panels cleanly sit above the
  // puzzle props without exiting the top of the screen. Prior values
  // at y=24/27.4 were fully above the fov half-extent and the panels
  // never drew to the framebuffer.
  const DOME_HEX_ROW_LOW_Y = 9;
  const DOME_HEX_ROW_HIGH_Y = 11;
  // World width ≈ 175u; 12 panels in 2 rows × 6 spans the whole ceiling
  // when spacing is ~26u. Row-2 is offset by half-spacing to interlock.
  const DOME_HEX_SPACING_X = 26;
  const DOME_HEX_START_X = 10;
  const DOME_HEX_PANELS_PER_ROW = 6;
  const DOME_PANEL_PLACEMENTS: ScatterPlacement[] = [];
  for (let i = 0; i < DOME_HEX_PANELS_PER_ROW; i++) {
    DOME_PANEL_PLACEMENTS.push({
      x: DOME_HEX_START_X + i * DOME_HEX_SPACING_X,
      y: DOME_HEX_ROW_LOW_Y,
      z: -8.6,
      yaw: 0,
      scale: 4.2,
    });
    DOME_PANEL_PLACEMENTS.push({
      // Offset by half-spacing so the rows interlock.
      x: DOME_HEX_START_X + i * DOME_HEX_SPACING_X + DOME_HEX_SPACING_X / 2,
      y: DOME_HEX_ROW_HIGH_Y,
      z: -9.0,
      yaw: 0,
      scale: 4.2,
    });
  }

  const vineScatter = await scatterInstances(
    scene,
    "/assets/models/environment/l2_iron_wood_vine.glb",
    VINE_PLACEMENTS,
    { shadowGenerator: shadowGen, tag: "l2_vine" },
  );
  const cakeScatter = await scatterInstances(
    scene,
    "/assets/models/environment/l2_petrified_layer_cake.glb",
    CAKE_PLACEMENTS,
    { shadowGenerator: shadowGen, tag: "l2_cake" },
  );
  const fernScatter = await scatterInstances(
    scene,
    "/assets/models/environment/l2_bioluminescent_fern.glb",
    FERN_PLACEMENTS,
    { shadowGenerator: shadowGen, tag: "l2_fern" },
  );
  const domePanelScatter = await scatterInstances(
    scene,
    "/assets/models/environment/l2_glass_dome_panel.glb",
    DOME_PANEL_PLACEMENTS,
    { shadowGenerator: shadowGen, tag: "l2_dome_panel" },
  );
  base.addDisposeCallback(() => {
    vineScatter.dispose();
    cakeScatter.dispose();
    fernScatter.dispose();
    domePanelScatter.dispose();
  });

  // Material polish for glass dome panels (hex frame + tinted glass)
  for (const inst of domePanelScatter.instances) {
    for (const mesh of inst.meshes) {
      const mat = mesh.material;
      if (mat instanceof PBRMaterial) {
        mat.metallic = 0.2;
        mat.roughness = 0.25;
        mat.alpha = 0.75;
      }
    }
  }

  // --- Ambient particles (flour dust / steam) ---
  const dustPS = createAmbientParticles(scene);
  // Register with CameraSystem so it is culled when zoomed far out (QA-gated).
  cameraSystem.registerAmbientParticles([dustPS]);

  // --- Canon Irrigation mist (book p.17) ---
  // Cool teal veil that blankets the dome during the Irrigation phase
  // so the cycle flip reads through particle density, not just lighting.
  // Emission rate is driven by the CONSERVATORY_CYCLE_CHANGED / legacy
  // DAY_NIGHT_CHANGED events: ~45/s when irrigation is active, 0 when
  // the dome is in incubation.
  const mistPS = createIrrigationMist(scene);
  const updateMistForPhase = (phase: "dawn" | "day" | "dusk" | "midnight"): void => {
    // Canonical: midnight/dusk → incubation (no mist), day/dawn → irrigation.
    const isIrrigation = phase === "day" || phase === "dawn";
    mistPS.emitRate = isIrrigation ? 48 : 0;
  };
  // Prime with the current phase so the mist is correct on spawn.
  updateMistForPhase(conservatoryCycle.phase);
  base.addUnsub(
    gameEvents.on(GameEvents.DAY_NIGHT_CHANGED, (data) => {
      updateMistForPhase(data.phase as "dawn" | "day" | "dusk" | "midnight");
    }),
  );
  base.addDisposeCallback(() => {
    mistPS.stop();
    mistPS.dispose();
  });

  // --- Shadow zones (ShadowSlip + ConservatoryCycle) ---
  const { shadowZoneInfos, shadowZoneMeshes } = createShadowZones(scene);
  conservatoryCycle.registerShadowZones(shadowZoneMeshes);
  shadowSlip.registerShadowZones(shadowZoneInfos);

  // Add players as shadow casters
  shadowGen.addShadowCaster(minna.mesh);
  shadowGen.addShadowCaster(basil.mesh);

  // --- Co-op abilities ---
  // Resonant Drop is part of the L2 arrival kit (Elder Kitty's L1 reward).
  // Chronos Bubble is registered up front so the activation path exists,
  // but it is THIS level's Elder reward: it stays locked until Elder Gus
  // grants it at the apex (or a prior completed run already unlocked it
  // in the save — replays keep the earned power).
  const resonantDrop = new ResonantDrop(scene, basil.mesh, minna.mesh);
  const chronosBubble = new ChronosBubble(scene, basil.mesh, minna.mesh);
  basilAbilities.addAbility(resonantDrop);
  basilAbilities.addAbility(chronosBubble);

  let chronosBubbleUnlocked = SaveSystem.getInstance().isCoopAbilityUnlocked(
    "chronosBubble",
  );
  base.addUnsub(
    gameEvents.on(GameEvents.COOP_ABILITY_UNLOCKED, ({ abilityId }) => {
      if (abilityId === "chronosBubble") chronosBubbleUnlocked = true;
    }),
  );

  // ─── Puzzle Modules ──────────────────────────────────────────────
  //
  // Section layout (world X positions):
  //   Section 1:  0-40   (Puzzle 1 - Rusted Pipe)
  //   Section 2: 40-70   (Puzzle 2 - Baking)
  //   Section 3: 70-100  (Puzzle 3 - Day/Night Lever)
  //   Section 4: 100-150 (Apex - Stealth Escort + Elder)

  const puzzles: PuzzleModule[] = [
    new MasterValvePuzzle(conservatoryCycle),
    new NectarEscortPuzzle(chronosBubble, conservatoryCycle),
    new BoilerBoundSundewPuzzle(conservatoryCycle, chronosBubble),
  ];

  // Level-scoped shared puzzle state. The apex Sundew gates on
  // `nectarEscort.solved`; the escort reads `conservatoryCycle.phase`
  // directly rather than going through levelState.
  const levelState = new Map<string, PuzzleState>();

  // AuraSight interactive collection — puzzles contribute their meshes
  // here via `ctx.registerInteractive`, then we hand the full list to the
  // ability once every puzzle finishes setup.
  const allInteractives: InteractiveInfo[] = [];

  const puzzleCtx: PuzzleContext = {
    scene,
    physicsSystem,
    glowLayer,
    shadowGenerator: shadowGen,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    auraSight,
    shadowSlip,
    calmingResonance,
    turnTableTouch,
    pneumaticAnchor,
    audioManager,
    cutsceneSystem,
    gameEvents,
    levelState,
    registerInteractive: (info) => {
      allInteractives.push(info);
    },
    addUnsub: (unsub) => base.addUnsub(unsub),
  };

  // Setup all puzzles. Sequential so async setup work runs in order.
  for (const puzzle of puzzles) {
    await puzzle.setup(puzzleCtx);
    levelState.set(puzzle.id, puzzle.getState());
  }

  // Expose loaded puzzle IDs for runtime parity testing against the
  // level's MechanicContract (see tests/integration/puzzle-id-parity.spec.ts).
  scene.metadata = {
    ...(scene.metadata ?? {}),
    loadedPuzzleIds: puzzles.map((p) => p.id),
  };

  // Wire Aura Sight with every contributed interactive.
  auraSight.registerInteractives(allInteractives);
  if (allInteractives.length >= 3) {
    // Connection hints: the first three interactives registered are the
    // Master Mist Regulator's two handles and the petrified vine choking
    // them, so Aura Sight draws the choke into the regulator it seizes.
    auraSight.registerConnections([
      [allInteractives[0], allInteractives[1]],
      [allInteractives[1], allInteractives[2]],
    ]);
  }

  // ─── Composite puzzle handle for QA tooling parity ───────────────
  const compositePuzzle = {
    id: "overgrownConservatoryComposite",
    getState: () => ({
      puzzles: Object.fromEntries(puzzles.map((p) => [p.id, p.getState()])),
    }),
    getInternals: () => ({
      masterValve: (puzzles[0] as MasterValvePuzzle).getInternals(),
      nectarEscort: (puzzles[1] as NectarEscortPuzzle).getInternals(),
      sundewApex: (puzzles[2] as BoilerBoundSundewPuzzle).getInternals(),
    }),
  };
  const levelHandleMeta = scene.metadata?.level as
    | {
        setPuzzle?: (p: unknown) => void;
        registerPuzzles?: (puzzles: PuzzleModule[], levelState: Map<string, PuzzleState>) => void;
      }
    | undefined;
  levelHandleMeta?.setPuzzle?.(compositePuzzle);
  // Expose per-puzzle modules to the QA harness: the hybrid
  // `moveToTarget` / `traverse` / `activateNear` / `waitForEvent`
  // primitives read QAHints via `scene.metadata.level.getQAHints()`
  // which aggregates across this list. Also enables `forceSolvePuzzle`
  // + `__startAtPuzzle` to short-circuit earlier puzzles deterministically.
  levelHandleMeta?.registerPuzzles?.(puzzles, levelState);

  // ─── Elder Gus (hidden until encounter) ──────────────────────────

  const elderGusPos = new Vector3(155, 16, 0);
  const elderGus = new ElderGus(scene, elderGusPos);
  elderGus.mesh.setEnabled(false);
  shadowGen.addShadowCaster(elderGus.mesh);
  base.addDisposeCallback(() => elderGus.dispose());
  allInteractives.push({
    mesh: elderGus.mesh,
    hidden: false,
    state: "revealed",
  });

  // ── Making Biscuits anchor (Gus's woven throw) ──
  // Per SPEC-making-biscuits.md §2: "Gus's reading pillow / knit throw
  // on his leather chair." The throw sits on a ledge just west of the
  // Elder's spawn so Minna walks past it on the way to the encounter
  // and can return after. Hidden by default — revealed via Aura Sight.
  const gusBiscuit = setupElderBiscuitAnchor({
    scene,
    elder: "gus",
    minna,
    cutsceneSystem,
    position: new Vector3(elderGusPos.x - 5, elderGusPos.y - 0.4, 0.2),
    interactiveRegistry: allInteractives,
  });
  base.addDisposeCallback(() => gusBiscuit.dispose());

  // ─── Per-puzzle guidance (objective line + anchored cue chip) ─────
  //
  // Each puzzle's getCue() names its current step (the Sundew's follows
  // its four phases); the driver pins the first unsolved puzzle's cue to
  // the thing to act on, with an "(N/3)" objective line. It also drives
  // the top puzzle counter (1/3, 2/3, 3/3). See LevelCueDriver.
  const cueDriver = new LevelCueDriver(
    [
      { puzzle: puzzles[0], objective: "Turn the Master Mist Regulator" },
      { puzzle: puzzles[1], objective: "Lead the opossums to their nests" },
      { puzzle: puzzles[2], objective: "Unwire the Boiler-Bound Sundew" },
    ],
    gameEvents,
  ).attach(scene);
  base.addDisposeCallback(() => cueDriver.dispose());

  // ─── Exit portal — sealed until the apex Elder cutscene completes ──
  //
  // L1 has a Portal entity past the apex; L2 had nothing, so a player
  // had no visible "destination" anywhere in the 160-unit level — the
  // level ended via cutscene callback with zero north-star feedback.
  // A sealed portal at x=158 (just past the Sundew apex) gives the
  // player a goal to walk toward throughout the level; the Elder Gus
  // cutscene opens it in its level-complete callback, matching L1.
  //
  // Grounded at y=1: the earlier placement at y=16 floated the portal
  // 16 units above the only walkable surface (the floor tops out at
  // y=0), permanently outside both the camera frame and the cats'
  // reach — pure decoration where nobody could ever see it.
  const exitPortalConservatory = new Portal(scene, {
    key: "conservatoryPortal",
    position: new Vector3(158, 1, 0),
    label: "Portal to Clockwork Canopy",
  });
  allInteractives.push({
    mesh: exitPortalConservatory.rootMesh,
    hidden: false,
    state: "revealed",
  });
  base.addDisposeCallback(() => exitPortalConservatory.dispose());

  // ─── Level state (shell only) ────────────────────────────────────

  let elderEncounterDone = false;
  let levelComplete = false;

  // ─── Apex solve triggers the Elder encounter ─────────────────────
  base.addUnsub(gameEvents.on(GameEvents.PUZZLE_SOLVED, (data) => {
    if (levelComplete) return;
    if (data?.puzzleId !== "sundewApex") return;
    // Delay slightly so the Phase 3 dash-sync flourish animation can
    // play out before the Elder fade begins.
    setTimeout(() => startElderEncounter(), 1500);
  }));

  function startElderEncounter(): void {
    if (elderEncounterDone) return;
    elderEncounterDone = true;

    gameEvents.emit(GameEvents.ELDER_ENCOUNTER, {
      elder: "gus",
      level: "OvergrownConservatory",
    });

    const dialogueSteps: CutsceneStep[] = ELDER_GUS_ENCOUNTER.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    }));

    const steps: CutsceneStep[] = [
      // Canon reveal: per book p. 19, "the feral greenhouse melts away
      // into a quiet, plush parlor. At its center sits Gus." The
      // `reveal` step plays the parlor-dissolve curtain — a Victorian
      // wingback-chair silhouette fades over a warm plum wash and
      // dissolves outward via chromatic aberration revealing Gus.
      { type: "fade", fadeDirection: "out", duration: 800 },
      { type: "callback", fn: () => {
        elderGus.mesh.setEnabled(true);
      }},
      { type: "reveal", preset: "parlor-dissolve", duration: 2500 },
      { type: "fade", fadeDirection: "in", duration: 800 },
      { type: "pan", target: { x: elderGusPos.x, y: elderGusPos.y }, duration: 2000 },
      { type: "wait", duration: 500 },
      ...dialogueSteps,
      { type: "shake", duration: 500, intensity: 0.008 },
      { type: "callback", fn: () => {
        gameEvents.emit(GameEvents.COOP_ABILITY_UNLOCKED, {
          abilityId: "chronosBubble",
          abilityName: "Chronos Bubble",
        });
      }},
      { type: "wait", duration: 1000 },
      { type: "fade", fadeDirection: "out", duration: 1500 },
      { type: "callback", fn: () => {
        completeLevelSequence();
      }},
    ];

    cutsceneSystem.play(steps);
  }

  function completeLevelSequence(): void {
    levelComplete = true;
    gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "OvergrownConservatory" });
    // Open the exit portal as the completion spectacle (same beat as L1's
    // Elder Kitty callback) — the transition itself is handled globally in
    // main.ts via the LEVEL_COMPLETE listener.
    exitPortalConservatory.open();
  }

  // ─── Dispose puzzles + Elder + environment on scene teardown ─────

  base.addDisposeCallback(() => {
    for (const puzzle of puzzles) {
      try { puzzle.dispose(); } catch (e) { console.warn("Puzzle dispose error:", e); }
    }
    dustPS.stop();
    dustPS.dispose();
  });

  // ─── Wounds and Medicine: Grease Caches ─────────────────────────
  // Spec §4 places 2-3 caches per level for L1-L3. L2 damage sources
  // include steam vents and Fox hits, so a mid-map safety net matters.
  const greaseCachesL2 = [
    new GreaseCache(scene, {
      key: "greaseCache_L2_a",
      position: new Vector3(32, 1.2, 0),
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L2_b",
      position: new Vector3(78, 1.2, 0),
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L2_c",
      position: new Vector3(128, 1.2, 0),
      label: "Grease Cache",
    }),
  ];
  base.addDisposeCallback(() => {
    for (const cache of greaseCachesL2) {
      try { cache.dispose(); } catch (e) { console.warn("GreaseCache dispose error:", e); }
    }
  });

  if (envInstance) {
    const instance = envInstance;
    scene.onDisposeObservable.addOnce(() => {
      instance.dispose();
    });
  }

  // ─── Game loop ───────────────────────────────────────────────────

  base.registerGameLoop(
    (dt, sceneTime) => {
      const dtMs = dt * 1000;

      // Conservatory phase cycle update (system owned by the scene, driven every tick).
      conservatoryCycle.update(dtMs);
      // Q-034: grade tween rides the same clock as the light lerp. When
      // the cycle completes it synchronously emits CONSERVATORY_PHASE_CHANGED
      // inside update() above, the settle handler snaps exact finals, and
      // this tick becomes a no-op — no double-apply.
      postTween.update(dtMs);

      // Tick each puzzle and refresh its entry in levelState so later
      // puzzles (day/night, stealth escort) always see up-to-date
      // solved flags from their prerequisites.
      for (const puzzle of puzzles) {
        puzzle.update(dt, sceneTime);
        levelState.set(puzzle.id, puzzle.getState());
      }

      // Wounds/Medicine: tick grease caches so respawn timers advance.
      for (const cache of greaseCachesL2) {
        cache.tick(dt);
      }

      // Elder Gus idle bob + glow orbit (only matters once revealed).
      if (elderGus.mesh.isEnabled()) {
        elderGus.update(sceneTime, dt);
      }

      // Making Biscuits: tick the minigame + proximity prompt for Gus's throw.
      gusBiscuit.update(dt);

      // Co-op ability updates. AbilityHolder.update() already ticks cooldowns
      // and calls update() on anything active, but ResonantDrop/ChronosBubble
      // were added directly via addAbility above so they're handled by
      // basilAbilities.update() in the BaseLevel game loop.
    },
    {
      isLevelComplete: () => levelComplete,
      onAbilityKey: (key, pressed, released) => {
        // Biscuits (B / A / D) take priority when a session is active.
        if (gusBiscuit.onAbilityKey(key, pressed, released)) {
          return true;
        }

        // Co-op combos live on the scene shell — both Resonant Drop (Level 1
        // unlock) and Chronos Bubble (this level's unlock) need a trigger.
        if (key === "f+m" && pressed) {
          tryCoopAbility(resonantDrop, "resonantDrop");
          return true;
        }

        if (key === "g+n" && pressed) {
          tryCoopAbility(chronosBubble, "chronosBubble", {
            unlocked: chronosBubbleUnlocked,
          });
          return true;
        }

        // Wounds/Medicine: let Basil spend a nearby Grease Cache via
        // the interact key. We check before puzzles so the cache
        // preempts overlapping puzzle interactions (consume only
        // succeeds if Basil is actually in radius AND the harness
        // needs healing, so puzzle-adjacent caches don't steal input).
        if (key === "," && pressed) {
          for (const cache of greaseCachesL2) {
            if (cache.isReady && cache.isInInteractRange(basil.mesh.position)) {
              if (cache.consume(base.harnessWounds, basil.mesh.position)) {
                return true;
              }
            }
          }
        }

        // Give each puzzle a chance to handle the key, latest first; the
        // first to return `true` consumes the input. Regulator cranks stay
        // live after their puzzle is solved: when the Master Valve's old
        // mid-escort cranks sat at x=129/131, inside Turn-Table reach of the
        // Sundew maw, array order let them grab Basil's `1` during the apex
        // fight (tools/playtest l2-route, 2026-09-27). The escort's mist
        // valve now sits at x=114.5, out of the maw's reach, but latest-first
        // still keeps the apex first in line. The Sundew only claims `1`
        // when one of its own valves/nodes is in reach.
        for (const puzzle of [...puzzles].reverse()) {
          if (puzzle.onAbilityKey && puzzle.onAbilityKey(key, pressed, released)) {
            return true;
          }
        }
        return false;
      },
    },
  );

  // ─── Start ───────────────────────────────────────────────────────

  setTimeout(() => audioManager.startAmbient(), 500);
  gameEvents.emit(GameEvents.LEVEL_STARTED, "OvergrownConservatory");

  // Opening cutscene with control hints.
  const introLines = LEVEL_INTRO["OvergrownConservatory"] ?? [];
  const introSteps: CutsceneStep[] = [
    { type: "fade", fadeDirection: "in", duration: 1200, color: { r: 0, g: 0, b: 0 } },
    { type: "wait", duration: 300 },
    { type: "pan", target: { x: 14, y: 7.5 }, duration: 900 },
    { type: "pan", target: { x: 42, y: 15.5 }, duration: 1200 },
    { type: "pan", target: { x: 8, y: 4 }, duration: 900 },
    ...introLines.map((line): CutsceneStep => ({
      type: "dialogue",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    })),
    { type: "dialogue", speaker: "", text: controlHintForMode(inputManager.soloMode), duration: 3500 },
    // The player arrives carrying Resonant Drop from Elder Kitty, but L2
    // never taught the chord anywhere — prompts only listed Q/E/F/1/2.
    { type: "dialogue", speaker: "", text: "Elder Kitty's gift travels with you — hold F + M together for Resonant Drop.", duration: 3500 },
    { type: "wait", duration: 300 },
  ];
  // `playDeferred` (not raw setTimeout) so that `__startAtPuzzle` can cancel
  // this pending intro before the timer fires — see
  // docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
  cutsceneSystem.playDeferred(introSteps, 600);

  return {
    physicsSystem,
    cameraSystem,
    inputManager,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    audioManager,
    conservatoryCycle,
    cutsceneSystem,
  };
}

// ─── Helper: Environment Loading ────────────────────────────────────

async function loadEnvironment(
  scene: Scene,
  // Unused since the 2026-07-10 receive-only shadow change; kept in the
  // signature so hero-prop or dynamic-env casters can re-opt-in later
  // without threading the generator through again.
  _shadowGen: ShadowGenerator,
): Promise<InstantiatedModel | null> {
  let envInstance: InstantiatedModel | null = null;
  try {
    envInstance = await getAssetManager().instantiateModel(
      scene,
      "/assets/models/environment/overgrown_conservatory_env.glb",
    );
  } catch (err) {
    console.warn("[OvergrownConservatory] Failed to load environment model:", err);
    return null;
  }

  for (const mesh of envInstance.meshes) {
    const sourceName = mesh.name.replace(/^Clone of /, "");
    if (sourceName.startsWith("collision_")) {
      mesh.isVisible = false;
      mesh.isPickable = false;
      new PhysicsAggregate(
        mesh,
        PhysicsShapeType.BOX,
        { mass: 0, friction: 0.8, restitution: 0.1 },
        scene,
      );
    } else if (sourceName === "conservatory_floor") {
      // The Blender-authored env GLB's floor mesh was exported with a
      // Z-up→Y-up axis conversion that flipped its depth dimension
      // onto Y: intended 120m-wide × 0.6m-thick × 14m-deep floor slab,
      // exported as a 120×14 wall standing at z=0.4 AND positioned at
      // world x=-47 — entirely outside the level. It was hidden for
      // months (every Gemini run flagged the "massive brown block");
      // the 2026-07-10 audit called the invisible authored floor L2's
      // cheapest visible win, so correct it in place instead: swap the
      // scaling axes back (120,14,0.6 → 120,0.6,14) and re-seat it so
      // its top sits flush with `collision_ground_fallback` (top y=0,
      // world center x=60 — the parent dome_frame node mirrors X, so
      // local -60). Physics stays on the fallback box; this mesh is
      // visual only. Verified live 2026-07-10 via the scene bridge.
      mesh.scaling.set(120, 0.6, 14);
      mesh.position.set(-60, -0.3, 0.4);
      mesh.isVisible = true;
      mesh.isPickable = false;
      mesh.receiveShadows = true;
    } else if (
      mesh.name !== "__root__" &&
      !mesh.name.startsWith("__root__") &&
      mesh.isVisible
    ) {
      // Receive-only: registering the whole dome shell as shadow
      // casters bloated the directional shadow frustum (see the
      // matching note in ClockworkCanopy's env loop).
      mesh.receiveShadows = true;
    }
  }

  // Freeze static scenery — iron ribs, glass panels, vines, moss,
  // ferns, cakes, pipes, boiler panels. Collision meshes stay unfrozen
  // because Havok mutates their transform internally.
  const staticMeshes = envInstance.meshes.filter((m) => {
    const source = m.name.replace(/^Clone of /, "");
    return !source.startsWith("collision_");
  });
  freezeStaticMeshes(staticMeshes, { excludePatterns: DEFAULT_STATIC_EXCLUDES });

  return envInstance;
}

async function loadConservatoryHeroProps(
  scene: Scene,
  shadowGen: ShadowGenerator,
): Promise<InstantiatedModel[]> {
  const placements = [
    {
      path: "/assets/models/environment/conservatory_mist_regulator.glb",
      position: new Vector3(22, 1.3, -2.1),
      scaling: new Vector3(2.8, 2.8, 2.8),
      rotationY: Math.PI / 2,
    },
    {
      path: "/assets/models/environment/conservatory_boiler_sundew.glb",
      position: new Vector3(133, 2.4, -2.8),
      scaling: new Vector3(2.8, 2.8, 2.8),
      rotationY: -Math.PI / 2,
    },
  ] as const;

  const instances: InstantiatedModel[] = [];
  for (const placement of placements) {
    try {
      const instance = await getAssetManager().instantiateModel(scene, placement.path);
      const root = instance.rootNodes[0];
      if (root) {
        root.position.copyFrom(placement.position);
        root.scaling.copyFrom(placement.scaling);
        root.rotationQuaternion = null;
        root.rotation.y = placement.rotationY;
      }
      for (const mesh of instance.meshes) {
        mesh.receiveShadows = true;
        shadowGen.addShadowCaster(mesh);
        tagAs(mesh, "final");
      }
      instances.push(instance);
    } catch (err) {
      console.warn("[OvergrownConservatory] Failed to load hero prop:", placement.path, err);
    }
  }

  return instances;
}

function createEscortSupportPlatforms(
  scene: Scene,
  physicsSystem: PhysicsSystem,
): Mesh[] {
  // Upper supports — the opossum nests and two opossums sit above the
  // ground route. Minna's runtime jump peak is ~2.2u above her capsule centre, so
  // ground-to-upper-support is unreachable without intermediate rungs.
  // The ladder rungs below staircase Minna up at ≤1.7u vertical gain
  // per jump and are offset horizontally so each rung's overhead stays clear when
  // she jumps off it. Ladder A approaches `escortSupportUpperA` from
  // the west (x<111) to avoid bonking into the upper support's own
  // footprint. Rungs stay out of the MasterValve puzzle zone (x≈21)
  // and the opening corridor (x=30-70) entirely.
  const supports = [
    physicsSystem.createPlatform(
      "escortSupportUpperA",
      8,
      0.4,
      OVERGROWN_CONSERVATORY.GROUND_DEPTH,
      new Vector3(115, 8, 0),
    ),
    physicsSystem.createPlatform(
      "escortSupportUpperB",
      7,
      0.4,
      OVERGROWN_CONSERVATORY.GROUND_DEPTH,
      new Vector3(125, 9, 0),
    ),
    // Ladder A — staircase up to escortSupportUpperA (y=8).
    physicsSystem.createPlatform(
      "escortLadderA_1",
      2.2,
      0.4,
      OVERGROWN_CONSERVATORY.GROUND_DEPTH,
      new Vector3(103.0, 1.6, 0),
    ),
    physicsSystem.createPlatform(
      "escortLadderA_2",
      2.2,
      0.4,
      OVERGROWN_CONSERVATORY.GROUND_DEPTH,
      new Vector3(105.4, 3.2, 0),
    ),
    physicsSystem.createPlatform(
      "escortLadderA_3",
      2.2,
      0.4,
      OVERGROWN_CONSERVATORY.GROUND_DEPTH,
      new Vector3(107.8, 4.8, 0),
    ),
    physicsSystem.createPlatform(
      "escortLadderA_4",
      2.2,
      0.4,
      OVERGROWN_CONSERVATORY.GROUND_DEPTH,
      new Vector3(110.2, 6.4, 0),
    ),
  ];

  // Re-skin the support platforms as faded mossy vine bridges instead of
  // leaving them invisible. The L2 QA pass flagged that opossums and the
  // player were traversing phantom geometry to reach the upper nests —
  // there was no visual indicator the platforms existed.
  // A translucent green tint reads as "soft vine bridge" without competing
  // with the puzzle interactives nearby for visual priority.
  for (const support of supports) {
    const mat = new StandardMaterial(`${support.name}Mat`, scene);
    mat.diffuseColor = new Color3(0.18, 0.42, 0.22);
    mat.emissiveColor = new Color3(0.05, 0.18, 0.08);
    mat.specularColor = Color3.Black();
    mat.alpha = 0.55;
    mat.backFaceCulling = false;
    support.material = mat;
    support.isVisible = true;
    tagAs(support, "modular-final");
  }

  return supports;
}

function createSundewMawPlatform(
  scene: Scene,
  physicsSystem: PhysicsSystem,
): Mesh {
  // Phase 3B asks Minna to hold Purr inside the Sundew chamber for six
  // seconds. The imported boss prop is visual-only, so provide a small
  // tongue-like standing surface whose top places the cat capsule center
  // inside the authored internal chamber trigger at y≈3.
  const platform = physicsSystem.createPlatform(
    "sundewMawTonguePlatform",
    3.6,
    0.35,
    OVERGROWN_CONSERVATORY.GROUND_DEPTH,
    new Vector3(133, 2.1, 0),
  );
  const mat = new StandardMaterial("sundewMawTonguePlatformMat", scene);
  mat.diffuseColor = new Color3(0.25, 0.08, 0.13);
  mat.emissiveColor = new Color3(0.12, 0.03, 0.08);
  mat.specularColor = Color3.Black();
  mat.alpha = 0.72;
  mat.backFaceCulling = false;
  platform.material = mat;
  platform.isVisible = true;
  tagAs(platform, "modular-final");
  return platform;
}

// ─── Helper: Procedural Conservatory Shell ───────────────────────────

function createConservatoryShell(
  scene: Scene,
  shadowGen: ShadowGenerator,
  collisionMeshes: readonly Mesh[],
): ConservatoryShellHandle {
  const root = new TransformNode("overgrownConservatory_conservatoryShell", scene);

  // Procedural PBR materials to pass the "untextured geometry" evaluator check.
  const makeMat = (
    name: string,
    diffuseHex: string,
    options?: { emissiveHex?: string; alpha?: number; bumpSeed?: string },
  ): PBRMaterial => {
    const mat = new PBRMaterial(name, scene);
    mat.albedoColor = Color3.FromHexString(diffuseHex);
    mat.metallic = 0.1;
    mat.roughness = 0.8;
    if (options?.emissiveHex) {
      mat.emissiveColor = Color3.FromHexString(options.emissiveHex);
    }
    if (options?.alpha !== undefined) {
      mat.alpha = options.alpha;
      mat.transparencyMode = PBRMaterial.MATERIAL_ALPHABLEND;
    }
    // Add procedural noise bump so the material isn't flagged as "untextured"
    const bump = getOrMakeNoiseBump(scene, options?.bumpSeed || name, 0.5);
    bump.uScale = 4;
    bump.vScale = 4;
    mat.bumpTexture = bump;
    return mat;
  };

  const ironMat = makeMat("burrowIronMat", "#1b2a28", { emissiveHex: "#0c1413", bumpSeed: "iron" });
  const brassMat = makeMat("burrowBrassMat", "#a06f2a", { emissiveHex: "#2e1808", bumpSeed: "brass" });
  const rootMat = makeMat("burrowRootMat", "#1c2d2a", { emissiveHex: "#0a1614", bumpSeed: "root" });
  const glassMat = makeMat("burrowGlassMat", "#6f8f72", {
    emissiveHex: "#1a261b",
    alpha: 0.18,
    bumpSeed: "glass",
  });
  const vineMat = makeMat("burrowVineMat", "#315233", { emissiveHex: "#162617", bumpSeed: "vine" });
  const blossomMat = makeMat("burrowBlossomMat", "#d2b183", { emissiveHex: "#493223" });
  const copperMat = makeMat("burrowCopperMat", COLORS.COPPER, { emissiveHex: "#2e1408" });
  const mistMat = makeMat("burrowMistMat", "#7aa37f", {
    emissiveHex: "#36543a",
    alpha: 0.08,
  });
  const heatVeilMat = makeMat("burrowHeatVeilMat", "#f6b06a", {
    emissiveHex: "#7a3515",
    alpha: 0.18,
  });
  const floraGlowMat = makeMat("burrowFloraGlowMat", "#c9d6a4", {
    emissiveHex: "#58c18a",
    alpha: 0.72,
  });
  const podGlowMat = makeMat("burrowPodGlowMat", "#d4a76c", {
    emissiveHex: "#6dc48d",
    alpha: 0.62,
  });
  const glowMat = makeMat("burrowGlowMat", "#a7d19f", {
    emissiveHex: COLORS.BIOLUMINESCENT_GREEN,
    alpha: 0.75,
  });

  const tag = (mesh: Mesh, castShadow = false): Mesh => {
    mesh.parent = root;
    mesh.receiveShadows = true;
    if (castShadow) {
      shadowGen.addShadowCaster(mesh);
    }
    tagAs(mesh, "modular-final");
    return mesh;
  };

  const worldCenterX = OVERGROWN_CONSERVATORY.WORLD_WIDTH / 2;
  // Cool conservatory mist backdrop (was dark olive brown, producing
  // the "brown wall covering 70% of the screen" Gemini flagged).
  const backdropMat = makeMat("burrowBackdropMat", "#0e1a1f", { emissiveHex: "#1a2e38" });
  const backdrop = tag(
    MeshBuilder.CreatePlane(
      "conservatoryBackdrop",
      { width: OVERGROWN_CONSERVATORY.WORLD_WIDTH * 1.12, height: 36 },
      scene,
    ),
  );
  backdrop.position.set(worldCenterX, 15, -11);
  backdrop.material = backdropMat;

  // The dome "band" is a huge horizontal cylinder simulating the
  // curved glass ceiling of the conservatory. Pushed from z=-10 to
  // z=-22 so its front face (at z=-5 after the shift) sits well
  // behind the cats' z=0 play plane. Gemini 3.1-pro deep review
  // flagged the prior placement as "a massive dark green/teal
  // rectangle blocking view at spawn" — the cylinder's front arc
  // was sitting 2u in front of the camera (at z=9 ➝ face at z=7),
  // and even at alpha 0.24 the compounded glass tint read as
  // opaque. Also raising y center so the lower arc clears player
  // eye-line.
  const domeBand = tag(
    MeshBuilder.CreateCylinder(
      "conservatoryDomeBand",
      { height: OVERGROWN_CONSERVATORY.WORLD_WIDTH * 0.98, diameter: 34, tessellation: 40 },
      scene,
    ),
  );
  domeBand.rotation.z = Math.PI / 2;
  domeBand.position.set(worldCenterX, 22, -22);
  domeBand.material = glassMat;

  const skylightRays: Mesh[] = [];
  const mistCurtains: Mesh[] = [];
  const heatVeils: Mesh[] = [];

  for (const cluster of [
    { x: 16, y: 28, sx: 34, sy: 10, rot: 0.22 },
    { x: 52, y: 30, sx: 44, sy: 12, rot: -0.18 },
    { x: 92, y: 31, sx: 48, sy: 13, rot: 0.08 },
    { x: 134, y: 29, sx: 42, sy: 11, rot: -0.16 },
    { x: 168, y: 27, sx: 30, sy: 9, rot: 0.24 },
  ]) {
    const canopy = tag(
      MeshBuilder.CreateSphere(
        `conservatoryRootCanopy_${cluster.x}`,
        {
          diameterX: cluster.sx,
          diameterY: cluster.sy,
          diameterZ: 4.5,
          segments: 18,
        },
        scene,
      ),
      true,
    );
    canopy.position.set(cluster.x, cluster.y, -7.1);
    canopy.rotation.z = cluster.rot;
    canopy.material = rootMat;
  }

  const skylightRim = tag(
    MeshBuilder.CreateTorus(
      "conservatorySkylightRim",
      { diameter: 14, thickness: 0.45, tessellation: 36 },
      scene,
    ),
    true,
  );
  skylightRim.rotation.x = Math.PI / 2;
  skylightRim.position.set(worldCenterX, 28.8, -7.9);
  skylightRim.material = ironMat;

  for (const [index, offset] of [-4.5, 0, 4.5].entries()) {
    const ray = tag(
      MeshBuilder.CreatePlane(
        `conservatorySkylightRay_${index}`,
        { width: 5.5, height: 20 },
        scene,
      ),
    );
    ray.position.set(worldCenterX + offset, 18.5, -8.35 + index * 0.02);
    ray.rotation.z = -0.08 + index * 0.04;
    ray.material = heatVeilMat;
    skylightRays.push(ray);
  }

  for (const ribX of [-8, 8, 24, 40, 56, 72, 88, 104, 120, 136, 152, 168]) {
    const arch = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryRib_${ribX}`,
        { height: 34, diameter: 0.55, tessellation: 18 },
        scene,
      ),
      true,
    );
    arch.rotation.z = Math.PI / 2;
    arch.position.set(ribX, 16, -8.2);
    arch.material = ironMat;
  }

  for (const beamY of [7, 11.5, 16, 20.5]) {
    const beam = tag(
      MeshBuilder.CreateBox(
        `conservatoryBeam_${beamY}`,
        { width: OVERGROWN_CONSERVATORY.WORLD_WIDTH + 12, height: 0.4, depth: 0.4 },
        scene,
      ),
      true,
    );
    beam.position.set(worldCenterX, beamY, -8.6);
    beam.material = ironMat;
  }

  for (const rootBand of [
    { x: 14, y: 9.5, len: 18, rot: 0.55 },
    { x: 42, y: 12.5, len: 22, rot: -0.48 },
    { x: 78, y: 10.8, len: 20, rot: 0.5 },
    { x: 126, y: 11.4, len: 24, rot: -0.42 },
    { x: 162, y: 9.8, len: 18, rot: 0.46 },
  ]) {
    const band = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryRootBand_${rootBand.x}`,
        { height: rootBand.len, diameter: 1.05, tessellation: 12 },
        scene,
      ),
      true,
    );
    band.position.set(rootBand.x, rootBand.y, -7.4);
    band.rotation.z = rootBand.rot;
    band.material = rootMat;
  }

  for (const paneX of [4, 20, 36, 52, 68, 84, 100, 116, 132, 148]) {
    const pane = tag(
      MeshBuilder.CreatePlane(
        `conservatoryPane_${paneX}`,
        { width: 11, height: 9 },
        scene,
      ),
    );
    pane.position.set(paneX, 11, -9.5);
    pane.material = glassMat;
  }

  const floorGlow = tag(
    MeshBuilder.CreatePlane(
      "conservatoryFloorGlow",
      { width: OVERGROWN_CONSERVATORY.WORLD_WIDTH * 1.08, height: 7 },
      scene,
    ),
  );
  floorGlow.position.set(worldCenterX, 2.4, -9.8);
  floorGlow.material = mistMat;

  for (const column of [
    { x: 18, y: 9.5, h: 12, w: 8 },
    { x: 58, y: 10, h: 13, w: 9 },
    { x: 96, y: 11, h: 14, w: 10 },
    { x: 132, y: 9.8, h: 12.5, w: 8.5 },
    { x: 160, y: 8.5, h: 10, w: 7.5 },
  ]) {
    const curtain = tag(
      MeshBuilder.CreatePlane(
        `conservatoryMistCurtain_${column.x}`,
        { width: column.w, height: column.h },
        scene,
      ),
    );
    curtain.position.set(column.x, column.y, -6.6);
    curtain.material = mistMat;
    mistCurtains.push(curtain);
  }

  for (const wash of [
    { x: 30, y: 16, w: 24, h: 11, rot: -0.12 },
    { x: 88, y: 17.5, w: 28, h: 12, rot: 0.08 },
    { x: 146, y: 15.5, w: 22, h: 10, rot: -0.05 },
  ]) {
    const veil = tag(
      MeshBuilder.CreatePlane(
        `conservatoryHeatVeil_${wash.x}`,
        { width: wash.w, height: wash.h },
        scene,
      ),
    );
    veil.position.set(wash.x, wash.y, -7.2);
    veil.rotation.z = wash.rot;
    veil.material = heatVeilMat;
    heatVeils.push(veil);
  }

  const platformSources = collisionMeshes.filter((mesh) => !mesh.name.includes("ground"));
  for (const collisionMesh of platformSources) {
    const bounds = collisionMesh.getBoundingInfo().boundingBox;
    const center = bounds.centerWorld;
    const width = Math.max(bounds.extendSizeWorld.x * 2, 2);
    const depth = Math.max(bounds.extendSizeWorld.z * 2, 0.7);
    const topY = bounds.maximumWorld.y;
    const supportHeight = Math.max(topY, 0.6);

    const deck = tag(
      MeshBuilder.CreateBox(
        `${collisionMesh.name}_deck`,
        { width: width * 0.98, height: 0.18, depth: depth * 0.85 },
        scene,
      ),
      true,
    );
    deck.position.set(center.x, topY + 0.08, -0.9);
    deck.material = brassMat;

    const planter = tag(
      MeshBuilder.CreateBox(
        `${collisionMesh.name}_planter`,
        { width: width * 0.82, height: 0.35, depth: depth * 0.68 },
        scene,
      ),
    );
    planter.position.set(center.x, topY + 0.34, -1.1);
    planter.material = ironMat;

    const leftSupport = tag(
      MeshBuilder.CreateCylinder(
        `${collisionMesh.name}_supportLeft`,
        { height: supportHeight, diameter: 0.22, tessellation: 10 },
        scene,
      ),
      true,
    );
    leftSupport.position.set(center.x - width * 0.32, supportHeight / 2, -1.15);
    leftSupport.material = ironMat;

    const rightSupport = tag(
      MeshBuilder.CreateCylinder(
        `${collisionMesh.name}_supportRight`,
        { height: supportHeight, diameter: 0.22, tessellation: 10 },
        scene,
      ),
      true,
    );
    rightSupport.position.set(center.x + width * 0.32, supportHeight / 2, -1.15);
    rightSupport.material = ironMat;

    const vine = tag(
      MeshBuilder.CreateCylinder(
        `${collisionMesh.name}_vine`,
        { height: Math.max(width * 0.6, 1.8), diameter: 0.12, tessellation: 8 },
        scene,
      ),
    );
    vine.rotation.z = 0.45;
    vine.position.set(center.x, topY + 1.0, -1.4);
    vine.material = vineMat;

    const blossom = tag(
      MeshBuilder.CreateSphere(
        `${collisionMesh.name}_blossom`,
        { diameter: 0.42, segments: 8 },
        scene,
      ),
    );
    blossom.position.set(center.x + width * 0.12, topY + 0.92, -1.35);
    blossom.material = blossomMat;
  }

  const groundCollision =
    collisionMeshes.find((mesh) => mesh.name.includes("ground")) ?? null;
  // The synthetic fallback floor spans the entire playable x range
  // (190 units) which, if used to size the walkway, produces a
  // frame-filling dark slab that blocks the camera. Detect the
  // fallback by name and skip the decorative walkway + planters
  // (they were only ever supposed to decorate the old burrow env's
  // narrower explicit ground collider).
  if (groundCollision && !groundCollision.name.includes("fallback")) {
    const bounds = groundCollision.getBoundingInfo().boundingBox;
    const center = bounds.centerWorld;
    const width = bounds.extendSizeWorld.x * 2;
    const topY = bounds.maximumWorld.y;

    const walkway = tag(
      MeshBuilder.CreateBox(
        "conservatoryWalkway",
        { width: width, height: 0.28, depth: 1.1 },
        scene,
      ),
      true,
    );
    walkway.position.set(center.x, topY + 0.14, -0.95);
    walkway.material = ironMat;

    for (const greenhouseX of [18, 42, 74, 108, 138]) {
      const planter = tag(
        MeshBuilder.CreateCylinder(
          `conservatoryPlanter_${greenhouseX}`,
          { height: 1.25, diameterTop: 1.6, diameterBottom: 1.2, tessellation: 14 },
          scene,
        ),
      );
      planter.position.set(greenhouseX, topY + 0.62, -1.9);
      planter.material = copperMat;

      const fronds = tag(
        MeshBuilder.CreateSphere(
          `conservatoryFronds_${greenhouseX}`,
          { diameterX: 2.2, diameterY: 1.2, diameterZ: 0.9, segments: 10 },
          scene,
        ),
      );
      fronds.position.set(greenhouseX, topY + 1.55, -2.2);
      fronds.material = vineMat;
    }
  }

  for (const pod of [
    { x: 28, y: 22.5, stem: 3.2, scale: 1.0 },
    { x: 63, y: 23.8, stem: 4.4, scale: 1.15 },
    { x: 92, y: 21.8, stem: 3.6, scale: 0.95 },
    { x: 121, y: 24.2, stem: 4.1, scale: 1.1 },
    { x: 152, y: 22.7, stem: 3.5, scale: 1.0 },
  ]) {
    const stem = tag(
      MeshBuilder.CreateCylinder(
        `conservatorySporeStem_${pod.x}`,
        { height: pod.stem, diameter: 0.12, tessellation: 8 },
        scene,
      ),
      true,
    );
    stem.position.set(pod.x, pod.y + pod.stem * 0.5, -2.9);
    stem.material = vineMat;

    const body = tag(
      MeshBuilder.CreateSphere(
        `conservatorySporePod_${pod.x}`,
        {
          diameterX: 1.15 * pod.scale,
          diameterY: 1.8 * pod.scale,
          diameterZ: 1.15 * pod.scale,
          segments: 12,
        },
        scene,
      ),
    );
    body.position.set(pod.x, pod.y - 0.15, -2.95);
    body.material = blossomMat;

    const glowCore = tag(
      MeshBuilder.CreateSphere(
        `conservatorySporeCore_${pod.x}`,
        { diameter: 0.55 * pod.scale, segments: 10 },
        scene,
      ),
    );
    glowCore.position.set(pod.x, pod.y - 0.1, -2.72);
    glowCore.material = podGlowMat;

    const ventCollar = tag(
      MeshBuilder.CreateCylinder(
        `conservatorySporeVent_${pod.x}`,
        { height: 0.24 * pod.scale, diameter: 0.82 * pod.scale, tessellation: 12 },
        scene,
      ),
    );
    ventCollar.position.set(pod.x, pod.y - 0.85 * pod.scale, -2.95);
    ventCollar.material = brassMat;
  }

  const ovenBody = tag(
    MeshBuilder.CreateCylinder(
      "conservatoryBoiler",
      { height: 4.4, diameter: 3.6, tessellation: 20 },
      scene,
    ),
    true,
  );
  ovenBody.position.set(12, 2.4, -2.8);
  ovenBody.material = copperMat;

  for (const [index, petal] of [
    { x: -1.8, y: 0.4, sx: 2.6, sy: 1.2, rot: -0.8 },
    { x: 1.95, y: 0.25, sx: 2.8, sy: 1.25, rot: 0.78 },
    { x: 0.2, y: 1.95, sx: 2.2, sy: 1.0, rot: 0.05 },
    { x: 0.15, y: -1.5, sx: 1.9, sy: 0.9, rot: -0.08 },
  ].entries()) {
    const sundewPetal = tag(
      MeshBuilder.CreateSphere(
        `conservatorySundewPetal_${index}`,
        {
          diameterX: petal.sx,
          diameterY: petal.sy,
          diameterZ: 0.42,
          segments: 12,
        },
        scene,
      ),
      true,
    );
    sundewPetal.position.set(12 + petal.x, 2.5 + petal.y, -1.55);
    sundewPetal.rotation.z = petal.rot;
    sundewPetal.material = vineMat;
  }

  const ovenDoor = tag(
    MeshBuilder.CreateBox(
      "conservatoryBoilerDoor",
      { width: 1.45, height: 1.45, depth: 0.18 },
      scene,
    ),
  );
  ovenDoor.position.set(12, 2.2, -1.15);
  ovenDoor.material = brassMat;

  const fireGlow = tag(
    MeshBuilder.CreateSphere(
      "conservatoryBoilerGlow",
      { diameter: 0.9, segments: 10 },
      scene,
    ),
  );
  fireGlow.position.set(12, 2.15, -0.95);
  fireGlow.material = glowMat;

  const boilerVine = tag(
    MeshBuilder.CreateTorus(
      "conservatoryBoilerVineRing",
      { diameter: 4.1, thickness: 0.18, tessellation: 24 },
      scene,
    ),
  );
  boilerVine.rotation.x = Math.PI / 2;
  boilerVine.position.set(12.2, 2.5, -1.8);
  boilerVine.material = vineMat;

  const portraitFrame = tag(
    MeshBuilder.CreateBox(
      "conservatoryPortraitFrame",
      { width: 1.35, height: 1.85, depth: 0.18 },
      scene,
    ),
  );
  portraitFrame.position.set(5.2, 8.8, -2.45);
  portraitFrame.material = brassMat;

  const portraitGlow = tag(
    MeshBuilder.CreatePlane(
      "conservatoryPortraitGlow",
      { width: 0.9, height: 1.25 },
      scene,
    ),
  );
  portraitGlow.position.set(5.2, 8.8, -2.32);
  portraitGlow.material = floraGlowMat;

  const mixingBoard = tag(
    MeshBuilder.CreateBox(
      "conservatoryMixingBoard",
      { width: 1.8, height: 1.65, depth: 0.16 },
      scene,
    ),
  );
  mixingBoard.position.set(6.7, 6.0, -2.5);
  mixingBoard.material = ironMat;

  for (const [index, leverX] of [-0.45, 0, 0.45].entries()) {
    const slot = tag(
      MeshBuilder.CreateBox(
        `conservatoryMixingSlot_${index}`,
        { width: 0.12, height: 0.82, depth: 0.05 },
        scene,
      ),
    );
    slot.position.set(6.7 + leverX, 5.95, -2.32);
    slot.material = brassMat;

    const lever = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryMixingLever_${index}`,
        { height: 0.62, diameter: 0.08, tessellation: 8 },
        scene,
      ),
      true,
    );
    lever.position.set(6.7 + leverX, 6.15 - index * 0.12, -2.22);
    lever.material = brassMat;
  }

  const wallLampStem = tag(
    MeshBuilder.CreateCylinder(
      "conservatoryWallLampStem",
      { height: 1.45, diameter: 0.08, tessellation: 8 },
      scene,
    ),
    true,
  );
  wallLampStem.position.set(8.8, 6.95, -2.34);
  wallLampStem.material = brassMat;

  const wallLamp = tag(
    MeshBuilder.CreateSphere(
      "conservatoryWallLamp",
      { diameterX: 0.45, diameterY: 0.78, diameterZ: 0.45, segments: 10 },
      scene,
    ),
  );
  wallLamp.position.set(8.8, 7.7, -2.18);
  wallLamp.material = glowMat;

  const prepTable = tag(
    MeshBuilder.CreateBox(
      "conservatoryPrepTable",
      { width: 4.8, height: 0.26, depth: 1.4 },
      scene,
    ),
    true,
  );
  prepTable.position.set(24, 1.4, -2.4);
  prepTable.material = brassMat;

  for (const legX of [-1.9, 1.9]) {
    const frontLeg = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryPrepLeg_front_${legX}`,
        { height: 1.2, diameter: 0.14, tessellation: 10 },
        scene,
      ),
      true,
    );
    frontLeg.position.set(24 + legX, 0.74, -1.95);
    frontLeg.material = ironMat;

    const backLeg = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryPrepLeg_back_${legX}`,
        { height: 1.2, diameter: 0.14, tessellation: 10 },
        scene,
      ),
      true,
    );
    backLeg.position.set(24 + legX, 0.74, -2.85);
    backLeg.material = ironMat;
  }

  const mixingBowl = tag(
    MeshBuilder.CreateCylinder(
      "conservatoryMixingBowl",
      { height: 0.55, diameterTop: 1.95, diameterBottom: 1.35, tessellation: 18 },
      scene,
    ),
  );
  mixingBowl.position.set(24.8, 1.75, -2.2);
  mixingBowl.material = copperMat;

  const cakeStand = tag(
    MeshBuilder.CreateCylinder(
      "conservatoryCakeStand",
      { height: 0.25, diameter: 1.2, tessellation: 14 },
      scene,
    ),
  );
  cakeStand.position.set(21.7, 1.55, -2.2);
  cakeStand.material = brassMat;

  for (const [index, cakeY] of [1.78, 2.03, 2.26].entries()) {
    const cake = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryCakeTier_${index}`,
        {
          height: 0.2,
          diameterTop: 0.9 - index * 0.12,
          diameterBottom: 1.04 - index * 0.12,
          tessellation: 16,
        },
        scene,
      ),
    );
    cake.position.set(21.7, cakeY, -2.2);
    cake.material = blossomMat;
  }

  for (const pipe of [
    { x: 4, y: 5.6, len: 12 },
    { x: 26, y: 4.4, len: 10 },
    { x: 58, y: 5.2, len: 14 },
    { x: 96, y: 4.8, len: 16 },
    { x: 132, y: 5.4, len: 12 },
  ]) {
    const run = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryPipe_${pipe.x}`,
        { height: pipe.len, diameter: 0.42, tessellation: 12 },
        scene,
      ),
      true,
    );
    run.rotation.z = Math.PI / 2;
    run.position.set(pipe.x, pipe.y, -3.6);
    run.material = copperMat;

    const vent = tag(
      MeshBuilder.CreateSphere(
        `conservatoryVent_${pipe.x}`,
        { diameter: 0.52, segments: 8 },
        scene,
      ),
    );
    vent.position.set(pipe.x + pipe.len * 0.28, pipe.y, -3.45);
    vent.material = brassMat;
  }

  for (const lantern of [
    { x: 14, y: 6.2, z: -2.4 },
    { x: 52, y: 6.8, z: -2.7 },
    { x: 93, y: 7.0, z: -2.6 },
    { x: 138, y: 6.4, z: -2.5 },
  ]) {
    const chain = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryLanternChain_${lantern.x}`,
        { height: 2.1, diameter: 0.08, tessellation: 8 },
        scene,
      ),
      true,
    );
    chain.position.set(lantern.x, lantern.y + 1.0, lantern.z);
    chain.material = ironMat;

    const body = tag(
      MeshBuilder.CreateBox(
        `conservatoryLantern_${lantern.x}`,
        { width: 0.42, height: 0.62, depth: 0.42 },
        scene,
      ),
    );
    body.position.set(lantern.x, lantern.y, lantern.z);
    body.material = brassMat;

    const lampGlow = tag(
      MeshBuilder.CreateSphere(
        `conservatoryLanternGlow_${lantern.x}`,
        { diameter: 0.28, segments: 8 },
        scene,
      ),
    );
    lampGlow.position.set(lantern.x, lantern.y, lantern.z);
    lampGlow.material = glowMat;
  }

  for (const growth of [
    { x: 18, y: 1.1, scale: 1.0 },
    { x: 36, y: 1.45, scale: 0.8 },
    { x: 76, y: 1.2, scale: 1.15 },
    { x: 118, y: 1.5, scale: 0.9 },
    { x: 148, y: 1.25, scale: 1.05 },
  ]) {
    const cakeBase = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryCake_${growth.x}`,
        {
          height: 0.55 * growth.scale,
          diameterTop: 1.05 * growth.scale,
          diameterBottom: 1.35 * growth.scale,
          tessellation: 14,
        },
        scene,
      ),
    );
    cakeBase.position.set(growth.x, growth.y, -2.2);
    cakeBase.material = blossomMat;

    const blossom = tag(
      MeshBuilder.CreateSphere(
        `conservatoryCakeBud_${growth.x}`,
        { diameter: 0.52 * growth.scale, segments: 8 },
        scene,
      ),
    );
    blossom.position.set(growth.x, growth.y + 0.42 * growth.scale, -2.05);
    blossom.material = floraGlowMat;
  }

  for (const fruit of [
    { x: 34, y: 18.7, scale: 0.8 },
    { x: 72, y: 19.5, scale: 0.95 },
    { x: 114, y: 18.9, scale: 0.85 },
    { x: 148, y: 19.2, scale: 0.9 },
  ]) {
    const vineStem = tag(
      MeshBuilder.CreateCylinder(
        `conservatoryCakeFruitStem_${fruit.x}`,
        { height: 2.2 * fruit.scale, diameter: 0.08, tessellation: 8 },
        scene,
      ),
      true,
    );
    vineStem.position.set(fruit.x, fruit.y + 1.15 * fruit.scale, -2.55);
    vineStem.material = vineMat;

    for (const [index, offsetY] of [0, 0.3, 0.56].entries()) {
      const fruitTier = tag(
        MeshBuilder.CreateCylinder(
          `conservatoryCakeFruit_${fruit.x}_${index}`,
          {
            height: 0.2 * fruit.scale,
            diameterTop: (0.72 - index * 0.08) * fruit.scale,
            diameterBottom: (0.86 - index * 0.08) * fruit.scale,
            tessellation: 14,
          },
          scene,
        ),
      );
      fruitTier.position.set(fruit.x, fruit.y + offsetY * fruit.scale, -2.45);
      fruitTier.material = blossomMat;
    }

    const fruitGlow = tag(
      MeshBuilder.CreateSphere(
        `conservatoryCakeFruitGlow_${fruit.x}`,
        { diameter: 0.34 * fruit.scale, segments: 8 },
        scene,
      ),
    );
    fruitGlow.position.set(fruit.x + 0.12, fruit.y + 0.62 * fruit.scale, -2.26);
    fruitGlow.material = floraGlowMat;
  }

  const applyPhase = (phase: "dawn" | "day" | "dusk" | "midnight"): void => {
    switch (phase) {
      case "midnight":
        glassMat.alpha = 0.1;
        mistMat.alpha = 0.2;
        heatVeilMat.alpha = 0.05;
        floraGlowMat.emissiveColor = Color3.FromHexString("#5dd48c");
        podGlowMat.emissiveColor = Color3.FromHexString("#67d9a0");
        backdropMat.emissiveColor = Color3.FromHexString("#08130d");
        break;
      case "dusk":
        glassMat.alpha = 0.14;
        mistMat.alpha = 0.14;
        heatVeilMat.alpha = 0.12;
        floraGlowMat.emissiveColor = Color3.FromHexString("#7cc892");
        podGlowMat.emissiveColor = Color3.FromHexString("#8dc894");
        backdropMat.emissiveColor = Color3.FromHexString("#142018");
        break;
      case "dawn":
        glassMat.alpha = 0.2;
        mistMat.alpha = 0.09;
        heatVeilMat.alpha = 0.16;
        floraGlowMat.emissiveColor = Color3.FromHexString("#b89d62");
        podGlowMat.emissiveColor = Color3.FromHexString("#c49358");
        backdropMat.emissiveColor = Color3.FromHexString("#1a150d");
        break;
      case "day":
      default:
        glassMat.alpha = 0.24;
        mistMat.alpha = 0.05;
        heatVeilMat.alpha = 0.2;
        floraGlowMat.emissiveColor = Color3.FromHexString("#d49f58");
        podGlowMat.emissiveColor = Color3.FromHexString("#c67d3d");
        backdropMat.emissiveColor = Color3.FromHexString("#20160f");
        break;
    }

    const mistVisible = phase === "midnight" || phase === "dusk";
    const heatVisible = phase === "day" || phase === "dawn";
    for (const curtain of mistCurtains) {
      curtain.isVisible = mistVisible;
    }
    for (const veil of heatVeils) {
      veil.isVisible = heatVisible;
    }
    for (const ray of skylightRays) {
      ray.isVisible = phase !== "midnight";
    }
  };

  return {
    root,
    applyPhase,
    dispose: () => root.dispose(),
  };
}

// ─── Helper: Ambient Particles (flour dust / steam) ─────────────────

function createAmbientParticles(scene: Scene): ParticleSystem {
  const W = OVERGROWN_CONSERVATORY.WORLD_WIDTH;
  const H = OVERGROWN_CONSERVATORY.WORLD_HEIGHT;

  const dustPS = new ParticleSystem("flourDust", 200, scene);
  dustPS.emitter = new Vector3(W / 2, H / 4, 0);
  dustPS.minLifeTime = 4;
  dustPS.maxLifeTime = 8;
  dustPS.minSize = 0.03;
  dustPS.maxSize = 0.08;
  dustPS.emitRate = 15;
  dustPS.gravity = new Vector3(0, 0.2, 0);
  dustPS.direction1 = new Vector3(-1, -0.3, -0.5);
  dustPS.direction2 = new Vector3(1, 0.5, 0.5);
  dustPS.color1 = new Color4(1, 0.9, 0.7, 0.2);
  dustPS.color2 = Color4.FromHexString(COLORS.COPPER + "33");
  dustPS.colorDead = new Color4(0, 0, 0, 0);
  dustPS.blendMode = ParticleSystem.BLENDMODE_ADD;
  dustPS.createBoxEmitter(
    new Vector3(-1, 0, -0.5), new Vector3(1, 1, 0.5),
    new Vector3(-W / 2, 0, -1), new Vector3(W / 2, H / 2, 1),
  );
  dustPS.start();

  return dustPS;
}

/**
 * Canon Irrigation-phase mist (book p.17).
 * Large, slow, cool-tinted particles filling the dome volume so the
 * cycle flip reads visually, not just via lighting. Fades in/out as
 * the `CONSERVATORY_CYCLE_CHANGED` event reports phase transitions.
 */
function createIrrigationMist(scene: Scene): ParticleSystem {
  const W = OVERGROWN_CONSERVATORY.WORLD_WIDTH;
  const H = OVERGROWN_CONSERVATORY.WORLD_HEIGHT;

  const mistPS = new ParticleSystem("irrigationMist", 420, scene);
  mistPS.emitter = new Vector3(W / 2, H / 3, 0);
  mistPS.minLifeTime = 6;
  mistPS.maxLifeTime = 11;
  mistPS.minSize = 0.8;
  mistPS.maxSize = 2.6;
  // Start silent — the scene's phase watcher turns emission on/off.
  mistPS.emitRate = 0;
  mistPS.gravity = new Vector3(0, 0.04, 0);
  mistPS.direction1 = new Vector3(-0.2, -0.05, -0.1);
  mistPS.direction2 = new Vector3(0.2, 0.1, 0.1);
  mistPS.minEmitPower = 0.05;
  mistPS.maxEmitPower = 0.25;
  // Cool teal tint to contrast the warm amber dust. Low alpha so
  // stacked particles read as a veil, not a wall.
  mistPS.color1 = new Color4(0.55, 0.78, 0.92, 0.28);
  mistPS.color2 = new Color4(0.38, 0.62, 0.80, 0.22);
  mistPS.colorDead = new Color4(0.2, 0.4, 0.6, 0);
  mistPS.blendMode = ParticleSystem.BLENDMODE_STANDARD;
  mistPS.createBoxEmitter(
    new Vector3(-0.3, 0, -0.4), new Vector3(0.3, 0.4, 0.4),
    new Vector3(-W / 2, 0, -1.2), new Vector3(W / 2, H * 0.7, 1.2),
  );
  mistPS.start();
  return mistPS;
}

// ─── Helper: Shadow Zones ───────────────────────────────────────────

function createShadowZones(scene: Scene): {
  shadowZoneInfos: ShadowZoneInfo[];
  shadowZoneMeshes: Mesh[];
} {
  const shadowZoneDefs = [
    { position: new Vector3(10, 1, 0), width: 5, height: 1.5 },
    { position: new Vector3(45, 1, 0), width: 6, height: 1.5 },
    { position: new Vector3(65, 1, 0), width: 4, height: 1.2 },
    { position: new Vector3(105, 1, 0), width: 6, height: 2.0 },
    { position: new Vector3(125, 1, 0), width: 5, height: 1.8 },
    { position: new Vector3(145, 1, 0), width: 7, height: 2.0 },
  ];

  const shadowZoneMeshes: Mesh[] = [];
  const shadowZoneInfos: ShadowZoneInfo[] = [];

  for (let i = 0; i < shadowZoneDefs.length; i++) {
    const def = shadowZoneDefs[i];

    // Invisible trigger volume
    const trigger = MeshBuilder.CreateBox(
      `shadowZone_${i}`,
      { width: def.width, height: def.height, depth: 1.5 },
      scene,
    );
    trigger.position = def.position;
    trigger.isVisible = false;
    trigger.isPickable = false;

    // Atmospheric dark visual
    const visual = MeshBuilder.CreateBox(
      `shadowZoneVisual_${i}`,
      { width: def.width, height: def.height, depth: 1.5 },
      scene,
    );
    visual.position = def.position;
    visual.isVisible = WORLD_DEBUG.SHOW_SHADOW_ZONE_VISUALS;
    visual.isPickable = false;
    const darkMat = new StandardMaterial(`shadowZoneMat_${i}`, scene);
    darkMat.diffuseColor = new Color3(0.02, 0.01, 0.04);
    darkMat.emissiveColor = new Color3(0.01, 0.005, 0.02);
    darkMat.alpha = 0.45;
    visual.material = darkMat;

    shadowZoneMeshes.push(trigger);
    shadowZoneInfos.push({ mesh: trigger });
  }

  return { shadowZoneInfos, shadowZoneMeshes };
}
